import { apiFetch } from "./apiClient";

export interface InvoiceDto {
  invoiceId: number;
  invoiceNumber: string | null;
  invoiceDate: string;

  /** Room + extra bed, i.e. the room amount. Mirrored by roomPayableAmount. */
  subtotal: number;

  // The food side of the folio: mealsAmount is this room's chargeable meals,
  // restaurantAmount its room-service orders, and foodAmount the two
  // together — the granularity the complimentary decision is taken at, since
  // one flag comps both.
  mealsAmount: number;
  restaurantAmount: number;
  foodAmount: number;

  serviceChargeAmount: number;
  taxAmount: number;
  /** How taxAmount was billed: CGST + SGST in-state, IGST alone inter-state. */
  isInterState: boolean;
  cgstAmount: number;
  sgstAmount: number;
  igstAmount: number;
  discountAmount: number;
  totalAmount: number;
  paidAmount: number;
  balanceAmount: number;

  // --- The complimentary decision, and what it left payable ---------------
  // Every amount above is the bill AS RAISED and keeps its face value even on
  // a comped stay, so the hotel can still report the revenue it gave away.
  // These say what was actually collected; with nothing comped the two sets
  // are identical.
  isRoomComplimentary: boolean;
  isFoodComplimentary: boolean;
  roomPayableAmount: number;
  foodPayableAmount: number;

  // Tax and service charge follow the comp — charged only on the value
  // actually supplied for payment.
  serviceChargePayableAmount: number;
  taxPayableAmount: number;

  // What checkout asked the guest for, and what balanceAmount/status
  // reconcile against. On a comped invoice totalAmount EXCEEDS
  // paidAmount + balanceAmount; the gap is the comped value.
  totalPayableAmount: number;

  /** totalPayableAmount > 0. False means checkout collected nothing at all. */
  paymentRequired: boolean;

  status: string | null;
}

export interface RoomBookingDto {
  roomBookingId: number;
  bookingType: string | null;
  directBookingId: number | null;
  eventBookingId: number | null;
  roomId: number;
  roomNo: string | null;
  roomTypeName: string | null;
  startDateTime: string;
  endDateTime: string;
  ratePerNight: number;

  // The room's stay is on the house. `isComplimentary` is the long-standing
  // field (an event room's complimentary-room allowance); `isRoomComplimentary`
  // is the name checkout sets it by and reports it under. Same value, always.
  isComplimentary: boolean;
  isRoomComplimentary: boolean;

  /** The guest's food is on the house — both meals and room service. */
  isFoodComplimentary: boolean;

  extraBedCount: number;
  extraBedRatePerNight: number;

  // What this room was actually sold on — snapshotted at booking time, so it
  // keeps printing correctly even if the rate plan is later renamed/retired.
  ratePlanId: number | null;
  ratePlanName: string | null;
  mealPlanId: number | null;
  mealPlanName: string | null;

  // Which meals that plan covers — Breakfast, Dinner, and so on. Read LIVE
  // from the meal plan master rather than snapshotted, so it reflects the
  // plan as it stands today. Empty when no rate plan was chosen.
  //
  // This is how an EVENT room reports its meals: `meals` below stays empty
  // for those, because date-wise BookingMeal rows hang off a direct booking
  // only.
  mealTypes: RoomMealTypeDto[];

  // Per-room occupancy driving meal covers.
  adults: number | null;
  children: number | null;

  statusId: number;
  /** StatusMaster category "RoomBooking": CONFIRMED | CHECKED_IN |
   * CHECKED_OUT | CANCELLED | RELEASED. The stable system identifier and the
   * ONLY field to branch on -- `statusName` beside it is per-account display
   * text an account may rename at will. */
  statusCode: string | null;
  statusName: string | null;
  checkedInDateTime: string | null;
  checkedOutDateTime: string | null;
  cancelledDateTime: string | null;
  cancellationReason: string | null;
  invoice: InvoiceDto | null;

  // This room's date-wise meals — Included rows generated from the rate plan
  // plus any Additional/Complimentary rows added for it.
  meals: BookingMealDto[];
}

/** Maps to the backend's RoomMealTypeDto — one meal the room's plan covers. */
export interface RoomMealTypeDto {
  mealTypeId: number;
  mealTypeName: string | null;
  /** What ONE additional serving costs; plan meals are inside the nightly rate. */
  mealTypeDefaultRate: number;
}

/** Maps to the backend's BookingMealDto — one date-wise meal on a booking. */
export interface BookingMealDto {
  bookingMealId: number;
  directBookingId: number;
  roomBookingId: number | null;
  roomNo: string | null;
  mealTypeId: number;
  mealTypeName: string | null;
  mealDate: string;
  quantity: number;
  // "Included" | "Additional" | "Complimentary"
  chargeType: string;
  rate: number;
  amount: number;
  remarks: string | null;
  // "Active" | "Served" | "Cancelled"
  status: string;
  createdOn: string;
  modifiedOn: string | null;
}

/** Maps to the backend's AddBookingMealDto — a meal the rate plan does not
 * cover. `mealDates` empty applies the meal to every night of the stay. */
export interface AddBookingMealDto {
  roomBookingId: number | null;
  mealTypeId: number;
  mealDates: string[];
  quantity: number | null;
  // MealChargeType.Additional or MealChargeType.Complimentary — never "Included".
  chargeType: string;
  rate: number | null;
  remarks: string | null;
  createdBy: number | null;
}

export interface BookingGuestDto {
  bookingGuestId: number;
  guestType: string | null;
  name: string | null;
  age: number | null;
  idType: string | null;
  idNumber: string | null;
}

export interface BookingPaymentDto {
  bookingPaymentId: number;
  paymentOption: string | null;
  paymentMethod: string | null;
  amount: number;
  paymentDate: string;
  notes: string | null;
}

/** Where a booking came from — the backend's BookingSourceKind. */
export type BookingSourceKind =
  | "FRONT_DESK"
  | "OTA"
  | "WEBSITE"
  | "CHANNEL_MANAGER"
  | "TEST";

/** Maps to the backend's BookingSourceDto (see BookingSourceRules). */
export interface BookingSourceDto {
  kind: BookingSourceKind;
  // What to show: "Front desk", "Agoda from Aiosell", "Aiosell", …
  label: string;
  // The rest is set only for a booking Aiosell sent. otaBookingId is the
  // OTA's own booking number, the one the guest quotes; payAtHotel false
  // means the guest prepaid online; otaStatus is the OTA reservation's
  // status (BOOKED, NEEDS_ATTENTION, CANCELLED, NO_SHOW or IGNORED).
  channel: string | null;
  otaBookingId: string | null;
  payAtHotel: boolean | null;
  otaReservationId: number | null;
  otaStatus: string | null;
}

/** Who a booking is billed to for GST (the API's BookingBillToDto): the guest,
 * or a GST-registered company with its own billing address. */
export interface BookingBillToDto {
  customerType: "INDIVIDUAL" | "BUSINESS";
  gstin?: string | null;
  legalName?: string | null;
  tradeName?: string | null;
  gstRegistrationType?: string | null;
  address?: string | null;
  city?: string | null;
  stateCode?: string | null;
  pincode?: string | null;
}

/** Maps to the backend's DirectBookingDto — the real, live DirectBookings
 * resource. A single booking can span multiple rooms (`rooms`), each with
 * its own independent status/check-in/check-out/cancel lifecycle. */
export interface DirectBookingDto {
  directBookingId: number;
  accountId: number;
  branchId: number | null;
  customerId: number | null;
  customerName: string | null;
  guestName: string | null;
  guestPhone: string | null;
  guestEmail: string | null;
  guestAddress: string | null;
  /** GST bill-to; null/absent = not stated (the customer record applies). */
  billTo?: BookingBillToDto | null;
  // Street line is guestAddress; these four arrived with the 2026-10-12 API.
  guestCity?: string | null;
  guestState?: string | null;
  guestPinCode?: string | null;
  guestCountry?: string | null;
  adults: number;
  children: number;
  infants: number;
  checkInDate: string;
  checkOutDate: string;
  babyAmenities: number[] | null;
  specialRequests: string | null;
  statusId: number;
  statusName: string | null;
  /** StatusMaster category "RoomBooking". DRAFT, DRAFT_CANCELLED and
   * DRAFT_EXPIRED mark a booking still in (or abandoned in) the wizard's
   * draft hold. Absent only from an API older than the draft hold. */
  statusCode?: string | null;
  /** Until when a DRAFT's held rooms stay blocked (UTC ISO). Null for a
   * booking that never went through the draft hold. */
  holdExpiresAt?: string | null;
  paymentOption: string | null;
  paymentMethod: string | null;
  roomsAmount: number;
  // Chargeable meals only — Included/Complimentary rows contribute 0.
  mealsAmount: number;
  otherCharges: number;
  discountAmount: number;
  gstAmount: number;
  serviceChargeAmount: number;
  grandTotal: number;
  amountPaid: number;
  balanceDue: number;
  isActive: boolean;
  createdOn: string;
  // Absent only from an API older than the booking source.
  source?: BookingSourceDto | null;
  rooms: RoomBookingDto[] | null;
  guests: BookingGuestDto[] | null;
  payments: BookingPaymentDto[] | null;
  // Every meal on this booking, date-wise — the rate plans' included meals
  // generated at creation plus anything added since.
  meals: BookingMealDto[] | null;
}

export interface CreateDirectBookingRoomDto {
  roomId: number;
  startDateTime: string;
  endDateTime: string;
  extraBedCount: number;

  // The rate plan this room is sold on — price AND meals both resolve from
  // it server-side. Null falls back to Room.Price with no meals (the rack
  // rate).
  ratePlanId: number | null;

  // Per-room occupancy driving meal covers. Null splits the booking's header
  // headcount evenly across its rooms.
  adults: number | null;
  children: number | null;

  // Extra meals for THIS room, on top of what its rate plan includes. Meals
  // belong on the room line because uniqueness is per room/date/meal — two
  // rooms both wanting lunch on the 10th is two valid rows, which a single
  // booking-level list cannot express. `roomBookingId` on these entries is
  // ignored; the server points them at the room this line creates.
  //
  // Optional: omitted means no extra meals for this room, which is what the
  // server assumes anyway.
  additionalMeals?: AddBookingMealDto[];
}

export interface CreateBookingGuestDto {
  guestType: string | null;
  name: string | null;
  age: number | null;
  idType: string | null;
  idNumber: string | null;
}

export interface CreateDirectBookingDto {
  accountId: number;
  branchId: number | null;
  customerId: number | null;
  guestName: string | null;
  guestPhone: string | null;
  guestEmail: string | null;
  guestAddress: string | null;
  /** GST bill-to; null/absent = not stated (the customer record applies). */
  billTo?: BookingBillToDto | null;
  // Street line is guestAddress; these four arrived with the 2026-10-12 API.
  guestCity?: string | null;
  guestState?: string | null;
  guestPinCode?: string | null;
  guestCountry?: string | null;
  adults: number;
  children: number;
  infants: number;
  checkInDate: string;
  checkOutDate: string;
  babyAmenities: number[];
  specialRequests: string | null;
  rooms: CreateDirectBookingRoomDto[];
  guests: CreateBookingGuestDto[];
  // BOOKING-LEVEL extra meals — ones not attributable to a single room
  // (stored with roombookingid NULL). To target a specific room, use that
  // room's own `additionalMeals` instead. As a convenience, on a single-room
  // booking these are attached to that room, since there is no ambiguity.
  //
  // Included meals are never listed here: the server generates those from
  // each room's rate plan.
  additionalMeals: AddBookingMealDto[];
  statusId: number;
  paymentOption: string | null;
  paymentMethod: string | null;
  amountPayingNow: number;
  /** Card / UPI / bank reference for the paying-now amount. */
  transactionReference?: string | null;
  otherCharges: number;
  discountAmount: number;
  createdBy: number | null;
}

export interface UpdateDirectBookingDto {
  customerId: number | null;
  /** With customerId: keep the typed guest details AND write them to that customer. */
  updateCustomerRecord?: boolean;
  guestName: string | null;
  guestPhone: string | null;
  guestEmail: string | null;
  guestAddress: string | null;
  /** GST bill-to; null/absent = not stated (the customer record applies). */
  billTo?: BookingBillToDto | null;
  // Street line is guestAddress; these four arrived with the 2026-10-12 API.
  guestCity?: string | null;
  guestState?: string | null;
  guestPinCode?: string | null;
  guestCountry?: string | null;
  adults: number;
  children: number;
  infants: number;
  checkInDate: string;
  checkOutDate: string;
  babyAmenities: number[];
  specialRequests: string | null;
  rooms: CreateDirectBookingRoomDto[];
  guests: CreateBookingGuestDto[];
  statusId: number;
  paymentOption: string | null;
  paymentMethod: string | null;
  isActive: boolean;
  otherCharges: number;
  discountAmount: number;
  modifiedBy: number | null;
}

/** Maps to the backend's SaveDraftDirectBookingDto — what the wizard knows
 * when Next is clicked on the Rooms tab. Saving it holds the rooms. */
export interface SaveDraftDirectBookingDto {
  accountId: number;
  branchId: number | null;
  adults: number;
  children: number;
  infants: number;
  checkInDate: string;
  checkOutDate: string;
  rooms: CreateDirectBookingRoomDto[];
  createdBy: number | null;
}

/** Maps to the backend's KeepDraftDirectBookingDto — what the Guest Details
 * and Payment tabs hold when the wizard is left, saved on the draft so it can
 * be resumed from the bookings list. Every field may still be empty. */
export interface KeepDraftDirectBookingDto {
  guestName: string | null;
  guestPhone: string | null;
  guestEmail: string | null;
  guestAddress: string | null;
  /** GST bill-to; null/absent = not stated (the customer record applies). */
  billTo?: BookingBillToDto | null;
  // Street line is guestAddress; these four arrived with the 2026-10-12 API.
  guestCity?: string | null;
  guestState?: string | null;
  guestPinCode?: string | null;
  guestCountry?: string | null;
  specialRequests: string | null;
  guests: CreateBookingGuestDto[];
  paymentOption: string | null;
  paymentMethod: string | null;
  modifiedBy: number | null;
}

export interface RoomAvailabilityDto {
  roomId: number;
  roomNo: string | null;
  roomTypeId: number;
  roomTypeName: string | null;
  /** Floor area, NOT occupancy — the Rooms screen labels this "Area". */
  capacity: number;
  maxAdult: number | null;
  maxChild: number | null;
  /** The room's extra-bed allowance. NOT part of `maxOccupancy`. */
  maxExtraBed: number | null;
  /** maxAdult + maxChild — the standing beds, extra beds excluded. */
  maxOccupancy: number;
  /** maxOccupancy + maxExtraBed — the most this room could ever sleep. */
  maximumGuests: number;
  /** Extra beds the requested party forces. Null unless adults/children
   * were passed to the availability call. */
  requiredExtraBeds: number | null;
  /** False when the party cannot sleep here even with every extra bed the
   * room allows. Null unless adults/children were passed. */
  fitsParty: boolean | null;
  price: number;
  /** StatusMaster category "Room": AVAILABLE | OCCUPIED | RESERVED |
   * MAINTENANCE | CHECKED_OUT. Branch on this, never on `roomStatusName`. */
  roomStatusCode: string | null;
  roomStatusName: string | null;
  isAvailable: boolean;
}

export interface RecordDirectBookingPaymentDto {
  paymentOption: string | null;
  paymentMethod: string | null;
  amount: number;
  notes: string | null;
  createdBy: number | null;
}

export interface CancellationEligibilityDto {
  roomBookingId: number;
  directBookingId: number | null;
  guestName: string | null;
  roomNo: string | null;
  startDateTime: string;
  endDateTime: string;
  bookingSource: string | null;
  roomLineTotal: number;
  amountPaidTowardRoom: number;
  refundEligible: boolean;
  refundPercentage: number;
  refundAmount: number;
  cancellationFee: number;
  reason: string | null;
  policyApplied: string | null;
}

export interface CheckInRoomBookingDto {
  performedBy: number | null;
  remarks: string | null;
  /** When the guest arrived, ISO UTC. Omitted means "now". Never in the future. */
  checkedInDateTime?: string | null;
}

export interface CheckOutRoomBookingDto {
  discountAmount: number;

  /** Percentage discounts per kind of line, 0-100, taken off before tax. Omitted keeps what is stored. */
  roomDiscountPercent?: number;
  extraBedDiscountPercent?: number;
  mealDiscountPercent?: number;

  /** When the guest left, ISO UTC. Omitted means "now". Never in the future or before check-in. */
  checkedOutDateTime?: string | null;

  // The "this one's on us" decision, taken at checkout. Independent — either,
  // both or neither — and comping both completes checkout with no payment
  // collected.
  //
  // null is NOT the same as false: it leaves whatever the reservation already
  // carries, which is what lets a room comped at booking time check out
  // without being silently un-comped. Send false explicitly to revoke a comp.
  isRoomComplimentary: boolean | null;
  isFoodComplimentary: boolean | null;

  /** Must be 0 when nothing is payable — the API rejects collecting against a fully comped stay. */
  paymentAmount: number;
  paymentMethod: string | null;
  transactionReference: string | null;
  performedBy: number | null;
  remarks: string | null;
}

export interface CancelRoomBookingDto {
  cancellationReason: string | null;
  performedBy: number | null;
}

export interface DirectBookingListFilters {
  accountId?: number;
  branchId?: number | null;
  statusId?: number;
  /** Also return open drafts (DRAFT, DRAFT_EXPIRED) — never cancelled ones. */
  includeDrafts?: boolean;
}

const buildDirectBookingQuery = (params: object): string => {
  const qs = new URLSearchParams();
  for (const [key, value] of Object.entries(params as Record<string, unknown>)) {
    if (value !== undefined && value !== null) qs.set(key, String(value));
  }
  const str = qs.toString();
  return str ? `?${str}` : "";
};

export const listDirectBookings = async (
  filters: DirectBookingListFilters = {}
): Promise<DirectBookingDto[]> => {
  const res = await apiFetch(`/directbookings${buildDirectBookingQuery(filters)}`);
  return res.json();
};

/** One page of a list the server pages (Application/Common/PagedResult). */
export interface PagedResult<T> {
  items: T[];
  /** Rows in the whole filtered list, across every page. */
  total: number;
  page: number;
  pageSize: number;
}

export interface BookingRowDto {
  roomBookingId: number;
  /** A draft's own status for a draft, the room's otherwise. */
  statusCode: string | null;
  statusName: string | null;
  directBooking: DirectBookingDto;
}

export interface BookingStatusCountDto {
  statusCode: string;
  statusName: string;
  count: number;
}

/** GET /directbookings/rows — the Room Bookings grid, paged and counted on the server. */
export interface BookingRowsPageDto extends PagedResult<BookingRowDto> {
  /** Per status in the chosen branch, other filters ignored (the tiles). */
  statusCounts: BookingStatusCountDto[];
  /** Per status with every filter but the status one (the tab counts). */
  filteredStatusCounts: BookingStatusCountDto[];
  sources: string[];
  /** Every booking in the branch, other filters ignored (the Total tile).
   * `total` is the bookings matching the filters: the grid pages by booking. */
  bookingTotal?: number;
  overdueCount: number;
  dueSoonCount: number;
}

export interface BookingRowsQuery {
  branchId?: number | null;
  page: number;
  pageSize: number;
  search?: string;
  /** Row status codes to keep (a tab, or one status). */
  statusCodes?: string[];
  source?: string;
  /** yyyy-MM-dd: stays starting or ending on that local day. */
  date?: string;
  overdueOnly?: boolean;
}

export const listBookingRows = async (q: BookingRowsQuery): Promise<BookingRowsPageDto> => {
  const params = new URLSearchParams();
  params.set("page", String(q.page));
  params.set("pageSize", String(q.pageSize));
  if (q.branchId != null) params.set("branchId", String(q.branchId));
  if (q.search?.trim()) params.set("search", q.search.trim());
  if (q.statusCodes?.length) params.set("statusCodes", q.statusCodes.join(","));
  if (q.source) params.set("source", q.source);
  if (q.date) {
    params.set("date", q.date);
    params.set("utcOffsetMinutes", String(-new Date().getTimezoneOffset()));
  }
  if (q.overdueOnly) params.set("overdueOnly", "true");
  const res = await apiFetch(`/directbookings/rows?${params.toString()}`);
  return res.json();
};

export const getDirectBooking = async (
  id: number
): Promise<DirectBookingDto> => {
  const res = await apiFetch(`/directbookings/${id}`);
  return res.json();
};

export const createDirectBooking = async (
  payload: CreateDirectBookingDto
): Promise<DirectBookingDto> => {
  const res = await apiFetch("/directbookings", {
    method: "POST",
    body: JSON.stringify(payload),
  });
  return res.json();
};

export const updateDirectBooking = async (
  id: number,
  payload: UpdateDirectBookingDto
): Promise<DirectBookingDto> => {
  const res = await apiFetch(`/directbookings/${id}`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
  return res.json();
};

export const deleteDirectBooking = async (id: number): Promise<void> => {
  await apiFetch(`/directbookings/${id}`, { method: "DELETE" });
};

// ----- Draft hold: the booking wizard's Rooms-tab Next -----------------------
// Next saves the booking as a DRAFT and holds its rooms (the API answers 409
// when a room was taken meanwhile); the final Create Booking confirms that
// same draft. The wizard's Cancel keeps the draft (keepDraftBooking) and it
// is listed on the bookings page, where staff resume it or cancel it —
// cancelling (cancelDraftBooking) is what frees the rooms.

export const createDraftBooking = async (
  payload: SaveDraftDirectBookingDto
): Promise<DirectBookingDto> => {
  const res = await apiFetch("/directbookings/draft", {
    method: "POST",
    body: JSON.stringify(payload),
  });
  return res.json();
};

export const updateDraftBooking = async (
  id: number,
  payload: SaveDraftDirectBookingDto
): Promise<DirectBookingDto> => {
  const res = await apiFetch(`/directbookings/${id}/draft`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
  return res.json();
};

/** `keepalive` lets the request finish while the page is being unloaded. */
export const keepDraftBooking = async (
  id: number,
  payload: KeepDraftDirectBookingDto,
  { keepalive = false }: { keepalive?: boolean } = {}
): Promise<void> => {
  await apiFetch(`/directbookings/${id}/draft/keep`, {
    method: "POST",
    body: JSON.stringify(payload),
    keepalive,
  });
};

/** Cancels a draft and frees its rooms at once. */
export const cancelDraftBooking = async (
  id: number,
  { keepalive = false }: { keepalive?: boolean } = {}
): Promise<void> => {
  await apiFetch(`/directbookings/${id}/draft/cancel`, {
    method: "POST",
    body: JSON.stringify({ reason: null, performedBy: null }),
    keepalive,
  });
};

export const confirmDraftBooking = async (
  id: number,
  payload: CreateDirectBookingDto
): Promise<DirectBookingDto> => {
  const res = await apiFetch(`/directbookings/${id}/confirm`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
  return res.json();
};

/** `excludeDirectBookingId` leaves that booking's own rooms out of the
 * check, so a draft's held rooms still show as selectable for it. */
export const listRoomAvailability = async (
  accountId: number,
  branchId: number | null,
  checkIn: string,
  checkOut: string,
  excludeDirectBookingId: number | null = null
): Promise<RoomAvailabilityDto[]> => {
  const params = new URLSearchParams({
    accountId: String(accountId),
    checkIn,
    checkOut,
  });
  if (branchId !== null) params.set("branchId", String(branchId));
  if (excludeDirectBookingId !== null)
    params.set("excludeDirectBookingId", String(excludeDirectBookingId));
  const res = await apiFetch(`/directbookings/room-availability?${params}`);
  return res.json();
};

export const recordDirectBookingPayment = async (
  directBookingId: number,
  payload: RecordDirectBookingPaymentDto
): Promise<DirectBookingDto> => {
  const res = await apiFetch(`/directbookings/${directBookingId}/payments`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
  return res.json();
};

export const getCancellationEligibility = async (
  directBookingId: number,
  roomBookingId: number
): Promise<CancellationEligibilityDto> => {
  const res = await apiFetch(
    `/directbookings/${directBookingId}/rooms/${roomBookingId}/cancellation-eligibility`
  );
  return res.json();
};

export const checkInRoomBooking = async (
  directBookingId: number,
  roomBookingId: number,
  payload: CheckInRoomBookingDto
): Promise<RoomBookingDto> => {
  const res = await apiFetch(
    `/directbookings/${directBookingId}/rooms/${roomBookingId}/check-in`,
    { method: "POST", body: JSON.stringify(payload) }
  );
  return res.json();
};

export const checkOutRoomBooking = async (
  directBookingId: number,
  roomBookingId: number,
  payload: CheckOutRoomBookingDto
): Promise<RoomBookingDto> => {
  const res = await apiFetch(
    `/directbookings/${directBookingId}/rooms/${roomBookingId}/check-out`,
    { method: "POST", body: JSON.stringify(payload) }
  );
  return res.json();
};

/** Checks several rooms of one booking out together onto ONE invoice. */
export const checkOutRooms = async (
  directBookingId: number,
  payload: CheckOutRoomBookingDto & { roomBookingIds: number[] }
): Promise<RoomBookingDto[]> => {
  const res = await apiFetch(`/directbookings/${directBookingId}/check-out`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
  return res.json();
};

export const cancelRoomBooking = async (
  directBookingId: number,
  roomBookingId: number,
  payload: CancelRoomBookingDto
): Promise<RoomBookingDto> => {
  const res = await apiFetch(
    `/directbookings/${directBookingId}/rooms/${roomBookingId}/cancel`,
    { method: "POST", body: JSON.stringify(payload) }
  );
  return res.json();
};

/** The guest's whole address for display: "12 MG Road, Kochi, Kerala - 682016, India".
 * Older bookings keep it all in guestAddress, which then shows as it is. */
export const formatBookingGuestAddress = (b: {
  guestAddress?: string | null;
  guestCity?: string | null;
  guestState?: string | null;
  guestPinCode?: string | null;
  guestCountry?: string | null;
}): string =>
  [
    b.guestAddress?.trim(),
    b.guestCity?.trim(),
    [b.guestState?.trim(), b.guestPinCode?.trim()].filter(Boolean).join(" - "),
    b.guestCountry?.trim(),
  ]
    .filter(Boolean)
    .join(", ");
